/**
 * JWT Utility Functions
 * Decode JWT tokens without verification (to check expiration)
 */

interface JWTPayload {
  exp?: number;
  sub?: string;
  [key: string]: any;
}

/**
 * Decode JWT token without verification
 * Only use this to check expiration, not for authentication
 */
export function decodeJWT(token: string): JWTPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    // Decode the payload (second part)
    const payload = parts[1];
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
}

/**
 * Check if a JWT token is expired
 */
export function isTokenExpired(token: string): boolean {
  const payload = decodeJWT(token);
  if (!payload || !payload.exp) {
    return true; // Consider expired if no expiration
  }

  // exp is in seconds, Date.now() is in milliseconds
  const expirationTime = payload.exp * 1000;
  const now = Date.now();

  return now >= expirationTime;
}

/**
 * Get time until token expires in milliseconds
 * Returns null if token is invalid or expired
 */
export function getTimeUntilExpiration(token: string): number | null {
  const payload = decodeJWT(token);
  if (!payload || !payload.exp) {
    return null;
  }

  const expirationTime = payload.exp * 1000;
  const now = Date.now();
  const timeUntilExpiration = expirationTime - now;

  return timeUntilExpiration > 0 ? timeUntilExpiration : null;
}

/**
 * Check if token will expire within a given time window (in milliseconds)
 */
export function willTokenExpireWithin(token: string, windowMs: number): boolean {
  const timeUntilExpiration = getTimeUntilExpiration(token);
  if (timeUntilExpiration === null) {
    return true; // Already expired or invalid
  }

  return timeUntilExpiration <= windowMs;
}







